/** @jsx jsx */
import { jsx } from "@emotion/react";
import { Location } from "history";
import {
  createContext,
  ReactNode,
  useCallback,
  useContext,
  useEffect,
  useState,
} from "react";
import { useHistory, useLocation } from "react-router-dom";
import { noop } from "src/globalConsts";

const PathnameHistoryContext = createContext<Location[]>([]);
const PushHistoryContext = createContext<(location: Location) => void>(noop);

type Props = {
  children: ReactNode;
};

// NOTE: This provider cannot detect history.replace() or history.goBack()
// calls and rewrite history accordingly. So far this implementation is sufficient
const LocationHistoryProvider = ({ children }: Props) => {
  const currentLocation = useLocation();
  const { push } = useHistory();

  const [history, setHistory] = useState<Location[]>([]);

  const historyPush = useCallback(
    (location: Location): void => {
      setHistory((prevState) => prevState.slice(1));
      push(location);
    },
    [setHistory, push]
  );

  useEffect(() => {
    if (currentLocation?.pathname === history[0]?.pathname) {
      // update last location - can differ in search or state params
      setHistory((old) => [currentLocation, ...old.slice(1)]);
      return;
    }

    setHistory((old) => [currentLocation, ...old]);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentLocation]);

  const [_, ...previous] = history;

  return (
    <PathnameHistoryContext.Provider value={previous}>
      <PushHistoryContext.Provider value={historyPush}>
        {children}
      </PushHistoryContext.Provider>
    </PathnameHistoryContext.Provider>
  );
};

export const useLocationHistory = () => useContext(PathnameHistoryContext);
export const useHistoryPush = () => useContext(PushHistoryContext);

export default LocationHistoryProvider;
